// KiraWelcomePage — fetches the user's daily briefing, the server-owned
// welcome greeting + emotion (POST /api/kira/welcome-greeting), and the
// owner-scoped to-do rail (GET /api/kira/todos), then renders KiraWelcome.
// Dismiss ("Go to dashboard") → saves the check-in conversation as today's
// morning diary entry, then → /messenger/dashboard.
//
// The check-in conversation IS the morning diary entry now. The old scripted
// login check-in (components/diary/DiaryLoginCheckin.jsx) has been retired,
// so this is the single morning write path.
//
// NOTE: the greeting + its tone are now generated SERVER-SIDE (Session B).
// The old client-side path (POST /api/ai/chat with a hand-built tone_hint /
// situation nudge) has been removed — the server owns tone.
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import KiraWelcome from "../components/kira/KiraWelcome";
import { useKiraContext } from "../context/KiraContext";
import { AuthContext } from "../context/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

export default function KiraWelcomePage() {
  const navigate = useNavigate();
  const kira     = useKiraContext();
  const { user } = useContext(AuthContext);

  const [briefing, setBriefing]                 = useState(null);
  const [greeting, setGreeting]                 = useState("");
  const [emotion, setEmotion]                   = useState("");
  const [todos, setTodos]                       = useState(null);
  const [fallbackGreeting, setFallbackGreeting] = useState("");
  const [isLoadingGreeting, setIsLoadingGreeting] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token   = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const safeJson = async (p) => {
      try {
        const res = await p;
        if (!res || !res.ok) return null;
        return await res.json();
      } catch { return null; }
    };

    const run = async () => {
      // 1. Fetch the four briefing data points in parallel.  Each may
      //    legitimately fail or return a different shape — we degrade
      //    gracefully and let the cards show "—" / fallback copy.
      //    Note: there's no /api/attendance/today endpoint in the backend;
      //    the closest signal is /api/attendance/history (returns rows
      //    with check_in/check_out per day) — find today's row.
      const [leaveData, historyData, notifData, dmData] = await Promise.all([
        safeJson(fetch(`${API_BASE}/api/leave/mine`,                { headers })),
        safeJson(fetch(`${API_BASE}/api/attendance/history`,        { headers })),
        safeJson(fetch(`${API_BASE}/api/notifications/unread-count`,{ headers })),
        safeJson(fetch(`${API_BASE}/api/dm/conversations`,          { headers })),
      ]);

      // ── Leave balance: try a few common shapes
      let leaveBalance = null;
      const leaveList = Array.isArray(leaveData) ? leaveData
                       : Array.isArray(leaveData?.balances) ? leaveData.balances
                       : Array.isArray(leaveData?.data) ? leaveData.data
                       : [];
      const annual = leaveList.find(b => /annual/i.test(b?.type || ""));
      if (annual) {
        const total = Number(annual.total || 0);
        const used  = Number(annual.used  || 0);
        const cf    = Number(annual.carry_forward || 0);
        leaveBalance = Math.max(0, total + cf - used);
      } else if (typeof leaveData?.balance === "number") {
        leaveBalance = leaveData.balance;
      }

      // ── Attendance: derive today's status from the history rows.
      // Row shape per attendance/schemas.py AttendanceHistoryRow:
      //   { id, date: "YYYY-MM-DD", status, check_in: <datetime>, check_out, notes }
      const todayISO = new Date().toISOString().slice(0, 10);   // YYYY-MM-DD
      const rows     = Array.isArray(historyData) ? historyData : [];
      const todayRow = rows.find(r => String(r?.date || "").slice(0, 10) === todayISO);
      const attendance = todayRow ? {
        checked_in:    !!todayRow.check_in,
        check_in_time: todayRow.check_in || "",
      } : { checked_in: false, check_in_time: "" };

      // ── Approved leave spanning today (status case-insensitive; dates inclusive).
      // Note: /api/leave/mine returns LeaveRequest rows not LeaveBalance —
      // the legacy `leaveBalance` derivation above is the existing usage; this
      // is a separate pass that treats the same payload as request rows.
      const todayDate = todayISO;  // ISO compare avoids TZ surprises
      const onLeaveRequest = leaveList.find(r => {
        if (!r) return false;
        if (String(r.status || "").toLowerCase() !== "approved") return false;
        const sd = String(r.start_date || "").slice(0, 10);
        const ed = String(r.end_date   || "").slice(0, 10);
        return sd && ed && sd <= todayDate && todayDate <= ed;
      });
      const onLeaveToday   = !!onLeaveRequest;
      const leaveTypeToday = onLeaveRequest?.leave_type || null;

      // ── Notifications unread count
      const unreadNotifs = Number(
        notifData?.unread_count
        ?? notifData?.count
        ?? (Array.isArray(notifData) ? notifData.length : 0)
        ?? 0
      );

      // ── DM unread (sum across conversations)
      const convos = Array.isArray(dmData?.conversations) ? dmData.conversations
                   : Array.isArray(dmData) ? dmData
                   : [];
      const unreadDMs = convos.reduce(
        (acc, c) => acc + Number(c?.unread_count || c?.unread || 0),
        0
      );

      const briefingData = { attendance, leaveBalance, unreadDMs, unreadNotifs, onLeaveToday, leaveTypeToday };
      if (cancelled) return;
      setBriefing(briefingData);

      // 2. Server-owned greeting + emotion (Session B) AND the owner-scoped
      //    to-do rail (Session C), fetched in parallel. The server assembles
      //    the leave context + the user's OWN most-recent diary entry and
      //    returns { greeting, emotion }. The client builds NO tone hint.
      const firstName = user?.full_name?.split(" ")[0] || "there";
      const [greetData, todosData] = await Promise.all([
        (async () => {
          try {
            const res = await fetch(`${API_BASE}/api/kira/welcome-greeting`, {
              method:  "POST",
              headers: { ...headers, "Content-Type": "application/json" },
            });
            return res.ok ? await res.json() : null;
          } catch { return null; }
        })(),
        safeJson(fetch(`${API_BASE}/api/kira/todos`, { headers })),
      ]);

      if (cancelled) return;
      setGreeting(greetData?.greeting || `Hi ${firstName}, good to see you.`);
      setEmotion(greetData?.emotion || "happy");
      setTodos(Array.isArray(todosData?.items) ? todosData.items : []);
      setIsLoadingGreeting(false);
    };

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Welcome check-in → today's morning diary entry ────────────────────
  // The diary stores a turn list of {role, text} where role is "kira" or
  // "user" (see components/diary/MyDiaryCalendar.jsx, which keys alignment
  // off role === "user" and renders t.text). useKira's messages are
  // {role: "user"|"assistant", content, id, tag, action_card, ...}, so we
  // map across and carry ONLY role + text: ids, tags and action-card
  // payloads are app plumbing with no business inside the sealed diary.
  const toDiaryTurns = (msgs) =>
    (Array.isArray(msgs) ? msgs : [])
      .filter((m) => m && typeof m.content === "string" && m.content.trim())
      .map((m) => ({
        role: m.role === "user" ? "user" : "kira",
        text: m.content.trim(),
      }));

  const saveCheckinToDiary = (msgs) => {
    // Superadmin is excluded from the diary everywhere else, so keep that
    // here too (they never route to /welcome, but the fallback path in
    // utils/postLoginRedirect.js can land anyone here).
    if (user?.role === "superadmin") return;

    const turns = toDiaryTurns(msgs);
    // No entry unless the person actually said something. A transcript of
    // KIRA talking to itself is not a check-in, and feeding it to the
    // server-side tone inference would mean classifying our own greeting.
    if (!turns.some((t) => t.role === "user")) return;

    // The EXISTING owner-only write: POST /api/diary/checkin/morning →
    // save_morning_checkin → the _owned (user_id == caller) seal. No new
    // endpoint and no seal change; this is one more caller of a sealed
    // path. felt_morning and tone are inferred server-side from the
    // transcript, so nothing is sent for them here.
    // Fire-and-forget: the save must never block or delay the exit.
    const token = localStorage.getItem("token");
    try {
      fetch(`${API_BASE}/api/diary/checkin/morning`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body:    JSON.stringify({ morning_conversation: turns, skipped: false }),
      }).catch(() => {});
    } catch {
      /* non-blocking */
    }
  };

  // Mirror `greeting` into a ref so the fallback timeout can read its
  // current value at fire-time without re-creating the timeout each render.
  const greetingRef = useRef("");
  useEffect(() => { greetingRef.current = greeting; }, [greeting]);

  // 15-second fallback — if the API greeting hasn't arrived, show a
  // friendly canned line instead of leaving the user staring at dots.
  // Read the live value via greetingRef so a late API reply still wins.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFallbackGreeting(prev => {
        if (greetingRef.current) return prev;  // real greeting already arrived
        const firstName = user?.full_name?.split(" ")[0] || "there";
        return `Hi ${firstName}, good to see you. How can I help you today?`;
      });
      // Give the avatar a settled mood if the greeting call is still pending.
      setEmotion(prev => prev || "happy");
    }, 15000);
    return () => clearTimeout(timeout);
  }, [user]);

  const handleDismiss = () => {
    // Welcome → dashboard is the final hop of both post-login flows.
    // On the way out, the check-in conversation becomes TODAY's morning
    // diary entry. This is the ONLY morning write now: the old scripted
    // login check-in has been retired in the same change, so there is no
    // second save to race with over the same (user_id, entry_date) row.
    saveCheckinToDiary(kira.messages);
    // dismissWelcome() is preserved (marks the welcome consumed for this
    // session and keeps the existing morning-diary flow intact).
    kira.dismissWelcome();
    navigate("/messenger/dashboard");
  };

  return (
    <KiraWelcome
      greeting={greeting || fallbackGreeting}
      emotion={emotion}
      briefing={briefing}
      todos={todos}
      isLoading={isLoadingGreeting}
      onDismiss={handleDismiss}
      // Bounded check-in conversation — reuse the existing useKira pipeline
      // (same one the floating bubble uses). Send produces an inline KIRA
      // reply; it does NOT navigate. Dismiss remains the only way out.
      onSend={kira.sendMessage}
      messages={kira.messages}
      chatEmotion={kira.emotion}
      chatLoading={kira.isLoading}
    />
  );
}
